/**
 * @file PlanYearList.js
 * @description Displays a list of plan years with search, filter, and sorting functionality.
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../store/AuthContext';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import styles from './PlanYear.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { FilterModal, renderFilterIcon } from '../helpers/FilterComponent';
import {
    searchPlanYears,
    deletePlanYear,
    fetchPlanYearById,
    createPlanYear,
} from '../../services/planYearService';
import { formatDate, getNameForDuplicate } from '../../utils/utils';
import EmptyTableRow from '../ui/no_data_indicators/EmptyTableRow';
import PaginationComponent from '../helpers/PaginationComponent';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const SortOptions = {
    NAME: 'name',
    CODE: 'code',
    START_DATE: 'startDate',
    END_DATE: 'endDate',
    IS_OFF_CYCLE: 'isOffCycle',
};

const OrderOptions = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const ITEMS_PER_PAGE = 20;

const PlanYearList = () => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const { user } = useAuthContext();
    const [isLoading, setIsLoading] = useState(true);
    const [planYearList, setPlanYearList] = useState([]);
    const [selectedPlanYearIds, setSelectedPlanYearIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        sort: SortOptions.NAME,
        order: OrderOptions.ASCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const selectAllRef = useRef(null);

    useEffect(() => {
        loadPlanYears();
    }, [currentPage, sortConfig]);

    useEffect(() => {
        if (!initialLoadComplete) return;

        const timeoutId = setTimeout(() => {
            loadPlanYears();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        applyFilters();
    }, [planYearList, selections]);

    const loadPlanYears = async () => {
        try {
            setIsLoading(true);

            const response = await searchPlanYears(
                null,
                currentPage,
                ITEMS_PER_PAGE,
                searchTerm,
                sortConfig.sort,
                sortConfig.order,
            );

            setPlanYearList(response.viewModels);
            setTotalPages(response.pageTotal);
            setInitialLoadComplete(true);
        } catch (error) {
            console.error('Failed to fetch plan year list', error);
            if (error?.response?.data?.detail)
                Toast.error(error?.response?.data?.detail);
            else Toast.error(getText('msg_fe0a5602e343'));
        } finally {
            setIsLoading(false);
        }
    };

    const applyFilters = () => {
        let result = [...planYearList];
        Object.entries(selections).forEach(([column, selectedValues]) => {
            if (selectedValues && selectedValues.length > 0) {
                result = result.filter(planYear => {
                    if (column === 'isOffCycle') {
                        const value = planYear[column] ? 'No' : 'Yes';
                        return selectedValues.includes(value);
                    }
                    return selectedValues.includes(
                        planYear[column]?.toString(),
                    );
                });
            }
        });

        setFilteredData(result);
    };

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        loadPlanYears();
    };

    const getOptions = column => {
        return Array.from(
            new Set(
                planYearList.map(item => {
                    if (column === 'isOffCycle') {
                        return item[column] ? 'Yes' : 'No';
                    }
                    return item[column]?.toString();
                }),
            ),
        )
            .filter(Boolean)
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    const handleSearch = term => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const handleSort = sort => {
        setSortConfig({
            sort,
            order:
                sortConfig.sort === sort &&
                sortConfig.order === OrderOptions.ASCENDING
                    ? OrderOptions.DESCENDING
                    : OrderOptions.ASCENDING,
        });
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        let deletedCount = 0;
        const errors = [];

        for (const planYearId of selectedPlanYearIds) {
            try {
                await deletePlanYear(planYearId);
                deletedCount++;
            } catch (error) {
                console.error(
                    `Failed to delete plan year with ID ${planYearId}:`,
                    error,
                );
                if (error?.response?.data?.detail)
                    errors.push(error?.response?.data?.detail);
                else errors.push(planYearId);
            }
        }

        if (deletedCount > 0) {
            await loadPlanYears();
        }

        if (deletedCount === selectedPlanYearIds.length) {
            Toast.success(`${deletedCount} plan year(s) deleted successfully`);
        } else if (deletedCount > 0) {
            Toast.warning(
                `${deletedCount} out of ${selectedPlanYearIds.length} plan year(s) deleted successfully. Some deletions failed.`,
            );
        } else {
            Toast.error(getText('msg_8ad3a6d3f308'));
        }

        setSelectedPlanYearIds([]);
    };

    const handleDuplicate = async () => {
        if (selectedPlanYearIds.length !== 1) {
            Toast.error(getText('msg_712ade51d99f'));
            return;
        }

        try {
            const [planYearId] = selectedPlanYearIds;
            const planYearToDuplicate = await fetchPlanYearById(planYearId);

            if (!planYearToDuplicate) {
                throw new Error('Selected plan year not found');
            }

            const allPlanYearsResponse = await searchPlanYears(
                null,
                1,
                10,
                '',
                'endDate',
                'desc',
            );

            const allPlanYears = allPlanYearsResponse.viewModels || [];
            const planYearNames = allPlanYears.map(planYear => planYear.name);
            const planYearCodes = allPlanYears.map(planYear => planYear.code);

            const newName = getNameForDuplicate(
                planYearToDuplicate.name,
                planYearNames,
            );

            const newCode = getNameForDuplicate(
                planYearToDuplicate.code,
                planYearCodes,
            );

            const latestEndDate =
                allPlanYears.length > 0 && allPlanYears[0].endDate
                    ? new Date(allPlanYears[0].endDate)
                    : null;

            const originalStartDate = new Date(planYearToDuplicate.startDate);
            const originalEndDate = new Date(planYearToDuplicate.endDate);
            const durationInDays = Math.ceil(
                (originalEndDate - originalStartDate) / (1000 * 60 * 60 * 24),
            );

            let newStartDate;
            if (latestEndDate) {
                newStartDate = new Date(latestEndDate);
                newStartDate.setDate(newStartDate.getDate() + 1);
            } else {
                newStartDate = new Date(originalStartDate);
                newStartDate.setFullYear(newStartDate.getFullYear() + 1);
            }

            const newEndDate = new Date(newStartDate);
            newEndDate.setDate(newEndDate.getDate() + durationInDays);

            const newPlanYear = {
                ...planYearToDuplicate,
                id: 0,
                name: newName,
                code: newCode,
                startDate: newStartDate.toISOString().split('T')[0],
                endDate: newEndDate.toISOString().split('T')[0],
            };

            await createPlanYear(newPlanYear);
            await loadPlanYears();
            Toast.success(getText('msg_5ade24dea1dd'));
            setSelectedPlanYearIds([]);
        } catch (error) {
            console.error('Failed to duplicate plan year', error);
            if (error?.response?.data?.detail)
                Toast.error(error?.response?.data?.detail);
            else Toast.error(getText('msg_6594d20c55a7'));
        }
    };

    const handlePlanYearSelection = planYearId => {
        setSelectedPlanYearIds(prevSelection =>
            prevSelection.includes(planYearId)
                ? prevSelection.filter(id => id !== planYearId)
                : [...prevSelection, planYearId],
        );
    };

    const handleSelectAllChange = event => {
        setSelectedPlanYearIds(
            event.target.checked
                ? filteredData.map(planYear => planYear.id)
                : [],
        );
    };

    const handleViewPlanYear = id => {
        navigate(ROUTE_BUILDERS.planYearsView(id));
    };

    const handleAddPlanYear = () => {
        navigate(ROUTES.PLAN_YEAR_ADD);
    };

    const openDeleteConfirmationModal = () => {
        if (!selectedPlanYearIds.length) {
            Toast.error(getText('msg_9bd1b9956da8'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_b957efeb6067'),
            text: `This action will delete ${
                selectedPlanYearIds.length > 1
                    ? 'these plan years'
                    : 'this plan year'
            } and cannot be undone.`,
            onYes: () => handleDelete(),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(LogEventType.DeleteClicked, `Plan years delete clicked`);
    };

    const renderSortArrow = sort => {
        if (sortConfig.sort !== sort) {
            return null;
        }
        return (
            <span className="mx-2">
                <svg width="11" height="6" viewBox="0 0 11 6" fill="none">
                    <path
                        d={
                            sortConfig.order === OrderOptions.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center py-3">
                        <div className="d-flex flex-column align-items-start mb-4">
                            <p className="page-title card-title ms-4 mb-0">
                                {getText('msg_f2086c423428')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_d148b6971f03')}
                                <br />
                                {getText('msg_b3bad019becb')}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center align-self-baseline">
                            <span
                                type="button"
                                onClick={openDeleteConfirmationModal}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-trash3 me-2" />
                                {getText('msg_f6fdbe48dc54')}
                            </span>
                            <span
                                type="button"
                                onClick={handleDuplicate}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-copy me-2" />
                                {getText('msg_972d57379db3')}
                            </span>
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            <span
                                type="button"
                                onClick={handleAddPlanYear}
                                className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                            >
                                <i className="bi bi-plus-lg me-1" />
                                {getText('msg_61cc55aa0453')}
                            </span>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="text-center mt-5">
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        </div>
                    ) : (
                        <div className="table-responsive-xxl w-100">
                            <table className="table table-hover align-middle">
                                <thead className="custom-table-header table-light">
                                    <tr>
                                        <th
                                            className={`text-center checkbox-column-head`}
                                        >
                                            <input
                                                type="checkbox"
                                                className={`form-check-input`}
                                                ref={selectAllRef}
                                                checked={
                                                    selectedPlanYearIds.length ===
                                                        filteredData.length &&
                                                    filteredData.length > 0
                                                }
                                                onChange={handleSelectAllChange}
                                            />
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            className="position-relative"
                                            onClick={() =>
                                                handleSort(SortOptions.NAME)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_709a23220f2c')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.NAME,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openModal('name');
                                                }}
                                                className={`${styles.cursorPointer} pe-3`}
                                            >
                                                {renderFilterIcon(
                                                    selections['name']?.length >
                                                        0,
                                                )}
                                            </span>
                                            {activeModal === 'name' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeModal}
                                                />
                                            )}
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            className="position-relative"
                                            onClick={() =>
                                                handleSort(SortOptions.CODE)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_adac69379a62')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.CODE,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openModal('code');
                                                }}
                                                className={`${styles.cursorPointer} pe-3`}
                                            >
                                                {renderFilterIcon(
                                                    selections['code']?.length >
                                                        0,
                                                )}
                                            </span>
                                            {activeModal === 'code' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeModal}
                                                />
                                            )}
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                handleSort(
                                                    SortOptions.START_DATE,
                                                )
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_9d7ab1a55796')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.START_DATE,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openModal('startDate');
                                                }}
                                                className={styles.cursorPointer}
                                            >
                                                {renderFilterIcon(
                                                    selections['startDate']
                                                        ?.length > 0,
                                                )}
                                            </span>
                                            {activeModal === 'startDate' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeModal}
                                                />
                                            )}
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                handleSort(SortOptions.END_DATE)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_84b147812589')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.END_DATE,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openModal('endDate');
                                                }}
                                                className={styles.cursorPointer}
                                            >
                                                {renderFilterIcon(
                                                    selections['endDate']
                                                        ?.length > 0,
                                                )}
                                            </span>
                                            {activeModal === 'endDate' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeModal}
                                                />
                                            )}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.length === 0 && (
                                        <EmptyTableRow
                                            colSpan={5}
                                            title={getText('msg_c0e82ceb4d0a')}
                                            description={getText(
                                                'msg_519d3b678d87',
                                            )}
                                        />
                                    )}
                                    {filteredData.map(planYear => (
                                        <tr key={planYear.id}>
                                            <td className="text-center">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={selectedPlanYearIds.includes(
                                                        planYear.id,
                                                    )}
                                                    onChange={() =>
                                                        handlePlanYearSelection(
                                                            planYear.id,
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td
                                                className={styles.titleCell}
                                                onClick={() =>
                                                    handleViewPlanYear(
                                                        planYear.id,
                                                    )
                                                }
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={e => {
                                                    if (
                                                        e.key === 'Enter' ||
                                                        e.key === ' '
                                                    ) {
                                                        handleViewPlanYear(
                                                            planYear.id,
                                                        );
                                                    }
                                                }}
                                            >
                                                {planYear.name}
                                            </td>
                                            <td className={styles.subtitleCell}>
                                                {planYear.code}
                                            </td>
                                            <td className={styles.subtitleCell}>
                                                {formatDate(planYear.startDate)}
                                            </td>
                                            <td className={styles.subtitleCell}>
                                                {formatDate(planYear.endDate)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!isLoading && filteredData.length > 0 && (
                        <PaginationComponent
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PlanYearList;
